import dayjs, { Dayjs } from 'dayjs'
import { MeteoConditionsDTO } from './api/MeteoConditionsDTO'
import type { SimulationDTO } from './api/simulation/SimulationDTO'
import type { SimulationReportItem } from './api/simulation/SimulationReportItem'
import { TYPE_GEOMETRY } from '@enums/TypeGeometry'
import Point from '@arcgis/core/geometry/Point'
import { FuelModelHumidityDTO } from './api/simulation/FuelModelHumidityDTO'
import { SimulationListItem } from './simulation/SimulationListItem'
import { MeteoConditionsSendDTO } from './api/simulation/MeteoConditionsSendDTO'
import { MeteoConditions } from './MeteoConditions'
import { SimulationSendDTO } from './api/simulation/SimulationSendDTO'
import { FuelModelLoadDTO } from './api/simulation/FuelModelLoadDTO'
import { useSimulationFuelModelsStore } from '@stores/simulationFuelModelsStore'

export class Simulation {
  simulationListItem: SimulationListItem
  simulationGeometries?: number[][][]
  initDate: Dayjs
  hours: number
  timeInterval: number
  endDate: Dayjs
  observations: string
  typeGeometries: TYPE_GEOMETRY[]
  isLocalWindActive: boolean
  localWindResolution?: number
  isCustomFuelMoisture: boolean
  isCustomMeteo?: boolean
  useFirewall: boolean
  firewalls?: any[] | null
  firewallType: TYPE_GEOMETRY[]
  meteoConditions: MeteoConditionsDTO[]
  fuelModels?: any[] | null
  fuelModelAreas?: any[] | null
  simulationReport?: SimulationReportItem[]
  areaAfected?: number
  medianSlope?: number
  medianSpreadRate?: number
  medianFlameLength?: number
  municipality?: string
  simulationCenter?: Point
  modeFuelModel?: string
  maxArea?: number
  fuelModelHumidity?: FuelModelHumidityDTO[]
  fuelModelLoads: FuelModelLoadDTO[]
  spreadDirection?: string
  dominantPropagationDir?: SimulationDTO['DOMINANT_PROPAGATION_DIR']
  modeSpreadDirection?: SimulationDTO['MODE_SPREAD_DIRECTION']
  mfe: SimulationDTO['MFE']
  ringData: SimulationDTO['RING_DATA']
  simulationParams?: SimulationDTO['SIM_PARAMS']
  crownFlag: SimulationDTO['CROWN_FLAG']
  foliarMoisture: SimulationDTO['FOLIAR_MOISTURE']
  crownFireMethod: SimulationDTO['CROWN_FIRE_FINNEY']
  tephigramData?: SimulationDTO['TEPHIGRAM_DATA']

  private constructor(serviceSimulation: SimulationDTO) {
    this.simulationListItem =
      SimulationListItem.fromSimulationDTO(serviceSimulation)
    this.observations = serviceSimulation.OBSERVATION
    this.initDate = dayjs(serviceSimulation.INIT_TIME)
    this.endDate = serviceSimulation.END_TIME
    this.hours = serviceSimulation.HOURS_NUM
    this.timeInterval = serviceSimulation.INTERVAL
    if (typeof serviceSimulation.GEOMETRY === 'string') {
      this.simulationGeometries = JSON.parse(serviceSimulation.GEOMETRY)
    } else {
      this.simulationGeometries = serviceSimulation.GEOMETRY as number[][][]
    }
    this.typeGeometries = serviceSimulation.GEOMETRY_TYPE
    this.useFirewall = Boolean(serviceSimulation.USE_FIREWALL)
    if (typeof serviceSimulation.FIREWALL === 'string') {
      try {
        this.firewalls = JSON.parse(serviceSimulation.FIREWALL)
      } catch (error) {
        console.error('Error parsing firewall', error)
      }
    } else {
      this.firewalls = serviceSimulation.FIREWALL
    }
    this.firewallType = serviceSimulation.FIREWALL_TYPE
    this.isLocalWindActive = Boolean(serviceSimulation.USE_WINDNINJA)
    this.localWindResolution = serviceSimulation.WINDNINJA_RESOLUTION
    this.isCustomFuelMoisture = Boolean(serviceSimulation.FUEL_MOISTURES_CONFIG)
    this.meteoConditions = serviceSimulation.METEO.map((meteo) =>
      MeteoConditionsDTO.fromMeteoConditionsSendDTO(meteo)
    )
    this.isCustomMeteo = serviceSimulation.CUSTOM_METEO
    this.fuelModels = serviceSimulation.FUEL_MODEL
    this.fuelModelAreas = serviceSimulation.FUEL_MODEL_AREA
    this.municipality = serviceSimulation.MUNICIPALITY
    this.fuelModelHumidity = serviceSimulation.FUEL_MOISTURES_CONFIG
    this.tephigramData = serviceSimulation.TEPHIGRAM_DATA
    this.crownFlag = serviceSimulation.CROWN_FLAG
    this.foliarMoisture = serviceSimulation.FOLIAR_MOISTURE
    this.crownFireMethod = serviceSimulation.CROWN_FIRE_FINNEY
    this.simulationCenter = Point.fromJSON(serviceSimulation.SIMULATION_CENTER)
    this.fuelModelLoads =
      serviceSimulation.INPUT_FMD ||
      useSimulationFuelModelsStore().defaultFuelModelPreset
        ?.fuelModelFileData ||
      []
  }

  toDTO(): SimulationDTO {
    return {
      ...this.simulationListItem.toPartialSimulationDTO(),
      OBSERVATION: this.observations,
      END_TIME: this.endDate,
      HOURS_NUM: this.hours,
      INTERVAL: this.timeInterval,
      GEOMETRY: JSON.stringify(this.simulationGeometries),
      GEOMETRY_TYPE: this.typeGeometries,
      USE_FIREWALL: this.useFirewall,
      FIREWALL: this.firewalls,
      FIREWALL_TYPE: this.firewallType,
      USE_WINDNINJA: this.isLocalWindActive,
      WINDNINJA_RESOLUTION: this.localWindResolution,
      METEO: this.meteoConditions?.map((meteo) =>
        MeteoConditionsSendDTO.fromMeteoConditionsDTO(meteo)
      ),
      CUSTOM_METEO: Boolean(this.isCustomMeteo),
      FUEL_MODEL: this.fuelModels,
      FUEL_MODEL_AREA: this.fuelModelAreas,
      SIMULATION_CENTER: this.simulationCenter?.toJSON(),
      MAX_AREA: this.maxArea,
      MEDIAN_FLAME_LENGTH: this.medianFlameLength,
      MEDIAN_SPREAD_RATE: this.medianSpreadRate,
      MEDIAN_SLOPE: this.medianSlope,
      MODE_FUEL_MODEL: this.modeFuelModel,
      MUNICIPALITY: this.municipality,
      FUEL_MOISTURES_CONFIG: this.fuelModelHumidity,
      INPUT_FMD: this.fuelModelLoads,
      SPREAD_DIRECTION: this.spreadDirection,
      MODE_SPREAD_DIRECTION: this.modeSpreadDirection,
      MFE: this.mfe,
      RING_DATA: this.ringData,
      SIM_PARAMS: this.simulationParams,
      DOMINANT_PROPAGATION_DIR: this.dominantPropagationDir,
      CROWN_FLAG: this.crownFlag,
      FOLIAR_MOISTURE: this.foliarMoisture,
      CROWN_FIRE_FINNEY: this.crownFireMethod
    }
  }

  static fromDTO(serviceSimulation: SimulationDTO) {
    const simulation = new Simulation(serviceSimulation)
    simulation.simulationListItem =
      SimulationListItem.fromSimulationDTO(serviceSimulation)
    simulation.municipality = serviceSimulation.MUNICIPALITY
    simulation.areaAfected = serviceSimulation.MAX_AREA
    simulation.medianSlope = serviceSimulation.MEDIAN_SLOPE
    simulation.medianSpreadRate = serviceSimulation.MEDIAN_SPREAD_RATE
    simulation.medianFlameLength = serviceSimulation.MEDIAN_FLAME_LENGTH
    simulation.simulationCenter = Point.fromJSON(
      serviceSimulation.SIMULATION_CENTER
    )
    simulation.modeFuelModel = serviceSimulation.MODE_FUEL_MODEL
    simulation.maxArea = serviceSimulation.MAX_AREA
    simulation.spreadDirection = serviceSimulation.SPREAD_DIRECTION
    simulation.modeSpreadDirection = serviceSimulation.MODE_SPREAD_DIRECTION
    simulation.mfe = serviceSimulation.MFE
    simulation.ringData = serviceSimulation.RING_DATA
    simulation.simulationParams = serviceSimulation.SIM_PARAMS
    simulation.dominantPropagationDir =
      serviceSimulation.DOMINANT_PROPAGATION_DIR
    return simulation
  }

  getMeteoConditionsValue(property: MeteoConditions['id']) {
    return this.meteoConditions?.find((meteo) => meteo.ID === property)?.VALORES
  }

  getMeteoConditionValue(property: MeteoConditions['id'], index: number) {
    return this.meteoConditions?.find((meteo) => meteo.ID === property)
      ?.VALORES[index]
  }

  getCoordinates() {
    return this.simulationGeometries?.map((geometry) => {
      return geometry.map((point) => {
        return { lat: point[1], lng: point[0] }
      })
    })
  }

  isCompleted() {
    return this.simulationListItem.isMapSimulationCompleted()
  }

  isFilesCompleted() {
    return this.simulationListItem.canDownloadFiles()
  }

  updateWithSimulationListItem(simulationListItem: SimulationListItem) {
    this.simulationListItem = simulationListItem
  }
}
